import React, { useState } from 'react';
import {
  Shield,
  AlertTriangle,
  CheckCircle,
  FileText,
  Users,
  Download,
  Eye,
  Edit,
  Plus,
  Search,
  Filter,
  ExternalLink,
  BookOpen,
  AlertCircle,
  TrendingUp,
  Target
} from 'lucide-react';
import { format } from 'date-fns';
import { 
  complianceRequirements, 
  complianceDashboardMetrics, 
  complianceAlerts,
  s8ComplianceRecords,
  mandatoryNotifications,
  cpdRecords
} from '../../data/complianceData';

const Compliance: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [showAlerts, setShowAlerts] = useState(true);

  const metrics = complianceDashboardMetrics;

  const getComplianceStatusColor = (status: string) => {
    switch (status) {
      case 'compliant': return 'bg-green-100 text-green-800';
      case 'non_compliant': return 'bg-red-100 text-red-800';
      case 'under_review': return 'bg-yellow-100 text-yellow-800';
      case 'action_required': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getAlertSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-50 border-red-200 text-red-800';
      case 'high': return 'bg-orange-50 border-orange-200 text-orange-800';
      case 'medium': return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'low': return 'bg-blue-50 border-blue-200 text-blue-800';
      default: return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'code_of_conduct': return <Users className="h-5 w-5 text-blue-600" />;
      case 'dispensing': return <FileText className="h-5 w-5 text-green-600" />;
      case 's8_management': return <Shield className="h-5 w-5 text-red-600" />;
      case 'compounding': return <Target className="h-5 w-5 text-purple-600" />;
      case 'notifications': return <AlertCircle className="h-5 w-5 text-orange-600" />;
      case 'cpd': return <BookOpen className="h-5 w-5 text-indigo-600" />;
      default: return <FileText className="h-5 w-5 text-gray-600" />;
    }
  };

  const filteredRequirements = complianceRequirements.filter(req => {
    const matchesCategory = selectedCategory === 'all' || req.category === selectedCategory;
    const matchesStatus = selectedStatus === 'all' || req.complianceStatus === selectedStatus;
    return matchesCategory && matchesStatus;
  });

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
            Compliance Management
          </h1>
          <p className="text-gray-600 mt-2">
            Australian Pharmacy Board regulations and compliance monitoring
          </p>
          <p className="text-sm text-gray-500 mt-1">
            Based on guidelines from{' '}
            <a 
              href="https://www.pharmacyboard.gov.au/Codes-Guidelines.aspx" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-purple-600 hover:text-purple-700 inline-flex items-center space-x-1"
            >
              <span>Pharmacy Board of Australia</span>
              <ExternalLink className="h-3 w-3" />
            </a>
          </p>
        </div>
        <div className="flex space-x-3">
          <button className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center space-x-2">
            <Plus className="h-4 w-4" />
            <span>New Audit</span>
          </button>
          <button className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 flex items-center space-x-2">
            <Download className="h-4 w-4" />
            <span>Export Report</span>
          </button>
        </div>
      </div>

      {/* Compliance Score Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Overall Compliance</p>
              <p className="text-4xl font-bold">{metrics.overallComplianceScore}%</p>
              <div className="flex items-center mt-2">
                <TrendingUp className="h-4 w-4 mr-1" />
                <span className="text-green-100 text-sm">+2.1% this month</span>
              </div>
            </div>
            <div className="p-4 rounded-2xl bg-white/20 backdrop-blur-sm">
              <Shield className="h-8 w-8" />
            </div>
          </div>
        </div>

        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Requirements</p>
              <p className="text-3xl font-bold text-gray-900">
                {metrics.compliantRequirements}/{metrics.totalRequirements}
              </p>
              <p className="text-sm text-green-600 mt-1">Compliant</p>
            </div>
            <div className="p-3 rounded-xl bg-blue-100">
              <CheckCircle className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">S8 Compliance</p>
              <p className="text-3xl font-bold text-gray-900">{metrics.s8ComplianceScore}%</p>
              <p className="text-sm text-green-600 mt-1">Excellent</p>
            </div>
            <div className="p-3 rounded-xl bg-red-100">
              <Shield className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">CPD Progress</p>
              <p className="text-3xl font-bold text-gray-900">{metrics.cpdCompletionRate}%</p>
              <p className="text-sm text-orange-600 mt-1">15 hours remaining</p>
            </div>
            <div className="p-3 rounded-xl bg-indigo-100">
              <BookOpen className="h-6 w-6 text-indigo-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Compliance Alerts */}
      {showAlerts && complianceAlerts.length > 0 && (
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <AlertTriangle className="h-6 w-6 text-orange-600" />
              <div>
                <h3 className="text-xl font-bold text-gray-900">Compliance Alerts</h3>
                <p className="text-gray-600 text-sm">Action required items and upcoming deadlines</p>
              </div>
            </div>
            <button 
              onClick={() => setShowAlerts(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>
          
          <div className="space-y-4">
            {complianceAlerts.map((alert) => (
              <div key={alert.id} className={`p-4 rounded-xl border ${getAlertSeverityColor(alert.severity)}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded-full ${
                      alert.severity === 'critical' ? 'bg-red-500' :
                      alert.severity === 'high' ? 'bg-orange-500' :
                      alert.severity === 'medium' ? 'bg-yellow-500' : 'bg-blue-500'
                    }`} />
                    <h4 className="font-semibold">{alert.title}</h4>
                  </div>
                  <span className="text-xs font-medium px-2 py-1 rounded bg-white/50">
                    {alert.type.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
                
                <p className="text-sm mb-3">{alert.message}</p>
                
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-600">
                    {alert.dueDate && (
                      <span>Due: {format(new Date(alert.dueDate), 'dd/MM/yyyy')}</span>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500">{alert.regulatoryReference}</span>
                    <button className="text-sm font-medium text-purple-600 hover:text-purple-700">
                      {alert.actionRequired}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search compliance requirements..."
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">All Categories</option>
            <option value="code_of_conduct">Code of Conduct</option>
            <option value="dispensing">Dispensing</option>
            <option value="s8_management">S8 Management</option>
            <option value="compounding">Compounding</option>
            <option value="notifications">Notifications</option>
            <option value="cpd">CPD</option>
          </select>

          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">All Statuses</option>
            <option value="compliant">Compliant</option>
            <option value="action_required">Action Required</option>
            <option value="under_review">Under Review</option>
            <option value="non_compliant">Non-Compliant</option>
          </select>

          <button className="flex items-center justify-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
            <Filter className="h-4 w-4" />
            <span>More Filters</span>
          </button>
        </div>
      </div>

      {/* Compliance Requirements */}
      <div className="space-y-6">
        {filteredRequirements.map((requirement) => (
          <div key={requirement.id} className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 hover:shadow-xl transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-start space-x-4">
                <div className="p-3 rounded-xl bg-gray-100">
                  {getCategoryIcon(requirement.category)}
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-xl font-semibold text-gray-900">{requirement.title}</h3>
                    <span className={`px-3 py-1 text-sm font-medium rounded-full ${getComplianceStatusColor(requirement.complianceStatus)}`}>
                      {requirement.complianceStatus.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-2">{requirement.description}</p>
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span>Source: {requirement.regulatorySource}</span>
                    <span>•</span>
                    <span>Effective: {format(new Date(requirement.effectiveDate), 'dd/MM/yyyy')}</span>
                    <span>•</span>
                    <span>Next Audit: {format(new Date(requirement.nextAuditDate), 'dd/MM/yyyy')}</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <button className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg">
                  <Eye className="h-4 w-4" />
                </button>
                <button className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg">
                  <Edit className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Requirements List */}
            <div className="mb-4">
              <h4 className="font-medium text-gray-900 mb-2">Key Requirements:</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {requirement.requirements.map((req, index) => (
                  <div key={index} className="flex items-center space-x-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700">{req}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Action Items */}
            {requirement.actionItems.length > 0 && (
              <div className="border-t pt-4">
                <h4 className="font-medium text-gray-900 mb-3">Action Items:</h4>
                <div className="space-y-2">
                  {requirement.actionItems.map((action) => (
                    <div key={action.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className={`w-3 h-3 rounded-full ${
                          action.priority === 'critical' ? 'bg-red-500' :
                          action.priority === 'high' ? 'bg-orange-500' :
                          action.priority === 'medium' ? 'bg-yellow-500' : 'bg-blue-500'
                        }`} />
                        <div>
                          <p className="font-medium text-gray-900">{action.description}</p>
                          <p className="text-sm text-gray-600">
                            Assigned to: {action.assignedTo} • Due: {format(new Date(action.dueDate), 'dd/MM/yyyy')}
                          </p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        action.status === 'completed' ? 'bg-green-100 text-green-800' :
                        action.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                        action.status === 'overdue' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {action.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Quick Access Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Recent S8 Audits */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <Shield className="h-6 w-6 text-red-600" />
              <div>
                <h3 className="text-lg font-bold text-gray-900">S8 Audits</h3>
                <p className="text-gray-600 text-sm">Recent compliance checks</p>
              </div>
            </div>
            <button className="text-purple-600 hover:text-purple-700 text-sm font-medium">View All</button>
          </div>
          
          <div className="space-y-4">
            {s8ComplianceRecords.slice(0, 3).map((record) => (
              <div key={record.id} className="p-3 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">
                    {record.type.replace('_', ' ').toUpperCase()}
                  </span>
                  <span className={`px-2 py-1 text-xs font-medium rounded ${
                    record.complianceStatus === 'compliant' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {record.complianceStatus.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
                <p className="text-sm text-gray-600">{format(new Date(record.date), 'dd/MM/yyyy')}</p>
                <p className="text-sm text-gray-700 mt-1">{record.findings[0]}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CPD Progress */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <BookOpen className="h-6 w-6 text-indigo-600" />
              <div>
                <h3 className="text-lg font-bold text-gray-900">CPD Progress</h3>
                <p className="text-gray-600 text-sm">Professional development</p>
              </div>
            </div>
            <button className="text-purple-600 hover:text-purple-700 text-sm font-medium">View All</button>
          </div>
          
          <div className="space-y-4">
            <div className="p-3 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-gray-900">2024 Progress</span>
                <span className="text-sm font-medium text-indigo-600">25/40 hours</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-indigo-600 h-2 rounded-full" style={{ width: '62.5%' }}></div>
              </div>
              <p className="text-sm text-gray-600 mt-2">15 hours remaining</p>
            </div>
            
            {cpdRecords.slice(0, 2).map((record) => (
              <div key={record.id} className="p-3 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{record.activityTitle}</span>
                  <span className="text-sm text-indigo-600">{record.hours}h</span>
                </div>
                <p className="text-sm text-gray-600">{format(new Date(record.date), 'dd/MM/yyyy')}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Mandatory Notifications */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <AlertCircle className="h-6 w-6 text-orange-600" />
              <div>
                <h3 className="text-lg font-bold text-gray-900">Notifications</h3>
                <p className="text-gray-600 text-sm">Mandatory reporting</p>
              </div>
            </div>
            <button className="text-purple-600 hover:text-purple-700 text-sm font-medium">View All</button>
          </div>
          
          <div className="space-y-4">
            {mandatoryNotifications.length > 0 ? (
              mandatoryNotifications.map((notification) => (
                <div key={notification.id} className="p-3 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">
                      {notification.type.replace('_', ' ').toUpperCase()}
                    </span>
                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                      notification.status === 'submitted' ? 'bg-blue-100 text-blue-800' :
                      notification.status === 'resolved' ? 'bg-green-100 text-green-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {notification.status.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{format(new Date(notification.reportDate), 'dd/MM/yyyy')}</p>
                  <p className="text-sm text-gray-700 mt-1">{notification.description.substring(0, 60)}...</p>
                </div>
              ))
            ) : (
              <div className="text-center py-4">
                <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                <p className="text-sm text-gray-600">No mandatory notifications required</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Compliance;
